import { HttpErrorResponse } from '@angular/common/http';
import { NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '../../../core/auth/auth';
import {
  Achievement,
  Competition,
  CompetitionsService,
  ContestantSummary,
  LeaderboardAward,
  LeaderboardEntry,
  Pick,
} from '../../../core/competitions/competitions';
import { AchievementsList } from '../achievements-list/achievements-list';
import { EntriesModal } from '../entries-modal/entries-modal';
import { ContestantModal } from '../contestant-modal/contestant-modal';
import { CompetitionEditModal } from '../competition-edit-modal/competition-edit-modal';
import { CompetitionMessage } from '../competition-message/competition-message';
import { CompetitionMyPicks } from '../competition-my-picks/competition-my-picks';

interface SelectedContestant {
  userId: number;
  displayName: string;
  email: string;
}

/** F-009 Milestone E — one leaderboard award flattened with its holder's
 *  name, for the admin awards management list. */
interface AdminAward extends LeaderboardAward {
  contestantDisplayName: string;
}

@Component({
  selector: 'app-competition-detail',
  imports: [
    RouterLink,
    NgTemplateOutlet,
    ReactiveFormsModule,
    AchievementsList,
    EntriesModal,
    ContestantModal,
    CompetitionEditModal,
    CompetitionMessage,
    CompetitionMyPicks,
  ],
  templateUrl: './competition-detail.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './competition-detail.css',
})
export class CompetitionDetail implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly competitions = inject(CompetitionsService);
  private readonly auth = inject(AuthService);

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly competition = signal<Competition | null>(null);
  readonly contestants = signal<ContestantSummary[]>([]);
  readonly achievements = signal<Achievement[]>([]);
  readonly copied = signal(false);
  readonly copyFailed = signal(false);

  /** F-015 — collapsed by default; message/dates/roster rules/achievements
   *  all live behind this so the picks list is the first thing seen. */
  readonly infoExpanded = signal(false);

  /** F-015 — the viewer's own picks, only fetched when they're actually a
   *  contestant (self-service GET :id/picks 404s for anyone else, e.g. a
   *  site admin just overseeing someone else's competition). */
  readonly myPicks = signal<Pick[]>([]);

  /** Entries modal (Slice 7) — this contestant's picks for this competition. */
  readonly entriesModalContestant = signal<SelectedContestant | null>(null);
  /** Contestant modal (Slice 8) — cross-competition history. */
  readonly infoModalContestant = signal<SelectedContestant | null>(null);
  /** Edit modal (F-009 Milestone C, Slice 7) — site-admin edit/delete. */
  readonly editModalOpen = signal(false);

  /** Kick (F-009 Milestone D, Slice 8) — admin-only in this milestone's
   *  contestants panel; the owner's panel never sets canManage, so these
   *  never surface there. */
  readonly confirmingKickUserId = signal<number | null>(null);
  readonly kicking = signal(false);
  readonly kickError = signal<string | null>(null);

  /** Add contestant by email (F-009 Milestone D, Slice 9) — admin-only,
   *  bypasses the invite-code flow entirely. */
  readonly addingContestant = signal(false);
  readonly addContestantError = signal<string | null>(null);
  readonly addContestantForm = new FormGroup({
    email: new FormControl('', { nonNullable: true }),
    /** F-028 — only used when the email doesn't match an existing
     *  account; ignored server-side otherwise. */
    displayName: new FormControl('', { nonNullable: true }),
  });

  /** F-009 Milestone E, Slice 10 — admin-only achievement removal. */
  readonly confirmingRemoveAchievementId = signal<number | null>(null);
  readonly removingAchievement = signal(false);
  readonly removeAchievementError = signal<string | null>(null);

  /** Backs both the admin awards-correction panel (F-009 Milestone E) and
   *  the viewer's own per-pick score breakdown (F-015) — same endpoint,
   *  already open to any member (owner/cohost/contestant/site-admin), so
   *  one fetch in load() covers both uses. */
  readonly leaderboard = signal<LeaderboardEntry[]>([]);
  readonly editingAwardId = signal<number | null>(null);
  readonly editAwardPointsControl = new FormControl<number | null>(null);
  readonly savingAward = signal(false);
  readonly confirmingRemoveAwardId = signal<number | null>(null);
  readonly removingAward = signal(false);
  readonly awardError = signal<string | null>(null);

  readonly adminAwards = computed<AdminAward[]>(() =>
    this.leaderboard().flatMap((entry) =>
      entry.awards.map((award) => ({
        ...award,
        contestantDisplayName: entry.displayName,
      })),
    ),
  );

  readonly isOwner = computed(() => {
    const c = this.competition();
    const user = this.auth.user();
    return !!c && !!user && c.ownerId === user.id;
  });

  /** F-009 Milestone C, Slice 7 — the site-admin-only panel (kick
   *  contestants, add by email, manage achievements/awards) is surfaced
   *  only when the viewer is a site admin managing someone else's
   *  competition, not for the owner.
   *  F-014 also reuses this to gate the "Admin-owned" badge — that's
   *  admin-facing info too: redundant for the owner to see about their
   *  own competition, and not something a regular contestant should be
   *  told about the organizer. */
  readonly showAdminControls = computed(
    () => this.auth.user()?.siteRole === 'SITE_ADMIN' && !this.isOwner(),
  );

  /** F-020 — who can open the edit/delete modal: the owner themselves, or
   *  a site admin managing someone else's competition. The backend's
   *  `RequireCompetitionRole('OWNER')` guard on PATCH/DELETE already
   *  allows exactly these two cases (owner, or site-admin bypass), so
   *  this mirrors that rather than introducing a new authorization rule. */
  readonly canEditCompetition = computed(() => this.isOwner() || this.showAdminControls());

  /** F-028 — quick-create-contestant needs to be reachable on an owner's
   *  OWN competition too when they're also a site admin (recreating their
   *  own real competition's historical entries is the actual use case),
   *  unlike showAdminControls()'s broader "someone else's competition"
   *  panel (kick, achievements/awards) — that panel's owner-exclusion is a
   *  deliberate, separate F-009 decision this doesn't touch. A non-admin
   *  owner still doesn't get this (they already have the invite-code
   *  flow); showAdminControls() already covers the non-owner case. */
  readonly canAddContestant = computed(
    () => this.auth.user()?.siteRole === 'SITE_ADMIN',
  );

  /** F-021 Milestone C — the "Manage achievements" link is owner-only and
   *  paid-tier-only, matching CompetitionAchievementsService's own gate;
   *  a FREE owner is stuck with the standard (fixed) achievements, so there's
   *  nothing for them to manage there. */
  readonly canManageAchievements = computed(
    () => this.isOwner() && this.competition()?.ownerPlan !== 'FREE',
  );

  readonly pastCutoff = computed(() => {
    const c = this.competition();
    return !c || new Date() > new Date(c.submissionEndDate);
  });

  readonly joinLink = computed(() => {
    const c = this.competition();
    return c ? `${window.location.origin}/join/${c.shortCode}` : '';
  });

  /** F-015 — true for the owner (auto-enrolled, decision #10) and any
   *  regular joined contestant; false for a cohost or site admin who
   *  isn't themselves playing this competition. */
  readonly isContestant = computed(() => {
    const userId = this.auth.user()?.id;
    return userId !== undefined && this.contestants().some((c) => c.id === userId);
  });

  /** F-015 — the single entry (if any) belonging to the current viewer,
   *  passed down to CompetitionMyPicks so it can merge points/prizes into
   *  each of the viewer's own picks by pickId. */
  readonly myLeaderboardEntry = computed(() => {
    const userId = this.auth.user()?.id;
    return this.leaderboard().find((entry) => entry.userId === userId) ?? null;
  });

  ngOnInit(): void {
    // Subscribed (not a one-time snapshot) so navigating card-to-card between
    // two different competition ids re-loads instead of reusing stale data.
    this.route.paramMap.subscribe((params) => {
      const id = Number(params.get('id'));
      void this.load(id);
    });
  }

  async copyJoinLink(): Promise<void> {
    try {
      // navigator.clipboard only exists in a secure context (HTTPS or
      // localhost) — dev is plain HTTP behind Apache, so it falls back to
      // the older execCommand technique there instead of just failing.
      if (window.isSecureContext) {
        await navigator.clipboard.writeText(this.joinLink());
      } else {
        this.legacyCopy(this.joinLink());
      }
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      // Clipboard access can still be denied (permissions) even in a
      // secure context — fail visibly instead of leaving an unhandled rejection.
      this.copyFailed.set(true);
      setTimeout(() => this.copyFailed.set(false), 2000);
    }
  }

  private legacyCopy(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const succeeded = document.execCommand('copy');
    document.body.removeChild(textarea);
    if (!succeeded) {
      throw new Error('execCommand copy failed');
    }
  }

  dateOnly(iso: string): string {
    return iso.slice(0, 10);
  }

  openEntries(contestant: ContestantSummary): void {
    this.entriesModalContestant.set({
      userId: contestant.id,
      displayName: contestant.displayName,
      email: contestant.email,
    });
  }

  /** F-032 follow-up — the modal only tracks picks locally, so the
   *  contestants list's pick/alternate counts (pickSummary() below) would
   *  otherwise stay stale after adding/removing picks in there until a
   *  full page reload. Re-fetches on close rather than threading a change
   *  event through the modal — simpler, and this list is small enough
   *  that one extra GET on close is cheap. */
  async closeEntriesModal(): Promise<void> {
    this.entriesModalContestant.set(null);
    const competition = this.competition();
    if (!competition) return;
    this.contestants.set(await this.competitions.listContestants(competition.id));
  }

  openInfo(contestant: ContestantSummary): void {
    this.infoModalContestant.set({
      userId: contestant.id,
      displayName: contestant.displayName,
      email: contestant.email,
    });
  }

  async onEditModalChanged(): Promise<void> {
    const id = this.competition()?.id;
    if (id !== undefined) {
      await this.load(id);
    }
  }

  async confirmKick(contestant: ContestantSummary): Promise<void> {
    const id = this.competition()?.id;
    if (id === undefined) return;

    this.kicking.set(true);
    this.kickError.set(null);
    try {
      await this.competitions.kick(id, contestant.id);
      this.confirmingKickUserId.set(null);
      await this.load(id);
    } catch (err) {
      this.kickError.set(this.errorMessageFor(err));
    } finally {
      this.kicking.set(false);
    }
  }

  async addContestant(): Promise<void> {
    const id = this.competition()?.id;
    if (id === undefined) return;

    const raw = this.addContestantForm.getRawValue();
    const email = raw.email.trim();
    if (!email) return;
    const displayName = raw.displayName.trim();

    this.addingContestant.set(true);
    this.addContestantError.set(null);
    try {
      await this.competitions.addContestant(
        id,
        email,
        displayName || undefined,
      );
      this.addContestantForm.reset({ email: '', displayName: '' });
      await this.load(id);
    } catch (err) {
      this.addContestantError.set(this.errorMessageFor(err));
    } finally {
      this.addingContestant.set(false);
    }
  }

  async confirmRemoveAchievement(achievement: Achievement): Promise<void> {
    const id = this.competition()?.id;
    if (id === undefined) return;

    this.removingAchievement.set(true);
    this.removeAchievementError.set(null);
    try {
      await this.competitions.removeAchievement(id, achievement.id);
      this.confirmingRemoveAchievementId.set(null);
      await this.load(id);
    } catch (err) {
      this.removeAchievementError.set(this.errorMessageFor(err));
    } finally {
      this.removingAchievement.set(false);
    }
  }

  startEditAward(award: AdminAward): void {
    this.awardError.set(null);
    this.editAwardPointsControl.setValue(award.points);
    this.editingAwardId.set(award.awardId);
  }

  async saveAwardEdit(award: AdminAward): Promise<void> {
    const id = this.competition()?.id;
    if (id === undefined) return;

    const points = this.editAwardPointsControl.value;
    this.savingAward.set(true);
    this.awardError.set(null);
    try {
      await this.competitions.updateAchievementAward(
        id,
        award.achievementId,
        award.awardId,
        { points: points ?? undefined },
      );
      this.editingAwardId.set(null);
      await this.load(id);
    } catch (err) {
      this.awardError.set(this.errorMessageFor(err));
    } finally {
      this.savingAward.set(false);
    }
  }

  async confirmRemoveAward(award: AdminAward): Promise<void> {
    const id = this.competition()?.id;
    if (id === undefined) return;

    this.removingAward.set(true);
    this.awardError.set(null);
    try {
      await this.competitions.removeAchievementAward(
        id,
        award.achievementId,
        award.awardId,
      );
      this.confirmingRemoveAwardId.set(null);
      await this.load(id);
    } catch (err) {
      this.awardError.set(this.errorMessageFor(err));
    } finally {
      this.removingAward.set(false);
    }
  }

  private errorMessageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const message = (err.error as { message?: string } | null)?.message;
      if (message) return message;
    }
    return 'Something went wrong — try again in a moment.';
  }

  pickSummary(contestant: ContestantSummary): string {
    const picks = `${contestant.pickCount} pick${contestant.pickCount === 1 ? '' : 's'}`;
    return contestant.alternateCount > 0
      ? `${picks} + ${contestant.alternateCount} alt`
      : picks;
  }

  private async load(id: number): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      // Sequential, not Promise.all — keeps zoneless test timing predictable
      // (matches the single-await-per-request pattern used elsewhere).
      const competition = await this.competitions.getOne(id);
      this.competition.set(competition);
      const achievements = await this.competitions.listAchievements(id);
      this.achievements.set(achievements);
      const contestants = await this.competitions.listContestants(id);
      this.contestants.set(contestants);

      // Same endpoint backs both the admin awards panel and the viewer's
      // own per-pick score breakdown (F-015) — already open to any member.
      this.leaderboard.set(await this.competitions.getLeaderboard(id));

      // F-015 — self-service GET :id/picks 404s for a non-contestant
      // (e.g. a site admin just overseeing someone else's competition),
      // so only fetch it when the viewer actually has a Contestant row.
      // isContestant() already reflects the contestants list just set above.
      this.myPicks.set(
        this.isContestant() ? await this.competitions.listPicks(id) : [],
      );
    } catch {
      this.errorMessage.set(
        'Could not load this competition — check the link or try again.',
      );
    } finally {
      this.loading.set(false);
    }
  }
}
